import { useState } from 'react';
import type { CoefficientResolutionDto } from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { CATALOG_VERSION_STATUS_LABELS } from '../../lib/api-types';
import type { ReferentialLifecycleAction } from '../../lib/api';
import { CodeNameForm, LifecycleButtons } from './shared';
import {
  useCoefficientSets,
  useCoefficientTypes,
  useCoefficientVersion,
  useCoefficientVersions,
  useCreateCoefficientSet,
  useCreateCoefficientVersion,
  useResolveCoefficients,
  useSetCoefficientValue,
  useTransitionCoefficientVersion,
} from './hooks';

/**
 * « Coefficients » : jeux de coefficients versionnés, valeurs (7 types, > 0), cycle de vie quatre-yeux
 * (l'auteur soumet, un validateur distinct approuve — 403 auto-approbation rendu verbatim), et un
 * testeur de résolution par date. Le serveur reste l'autorité (valeurs et transitions validées serveur).
 */
export function CoefficientsTab(): JSX.Element {
  const sets = useCoefficientSets();
  const types = useCoefficientTypes();
  const createSet = useCreateCoefficientSet();
  const [setId, setSetId] = useState('');
  const versions = useCoefficientVersions(setId, setId !== '');
  const createVersion = useCreateCoefficientVersion(setId);
  const [versionId, setVersionId] = useState('');
  const versionDetail = useCoefficientVersion(versionId, versionId !== '');
  const setValue = useSetCoefficientValue(versionId);
  const transition = useTransitionCoefficientVersion(versionId);

  const [showSetForm, setShowSetForm] = useState(false);
  const [valFrom, setValFrom] = useState('');
  const [valTo, setValTo] = useState('');
  const [valueDraft, setValueDraft] = useState<Record<string, string>>({});

  // Resolution tester
  const [rSetCode, setRSetCode] = useState('');
  const [rDate, setRDate] = useState('');
  const resolve = useResolveCoefficients();
  const [resolved, setResolved] = useState<CoefficientResolutionDto | null>(null);

  const version = versionDetail.data;
  const isDraft = version?.status === 'BROUILLON';

  const onLifecycle = (action: ReferentialLifecycleAction): void => {
    transition.mutate(action, { onSuccess: () => versionDetail.refetch() });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>Jeux de coefficients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {sets.isLoading ? <Spinner label="Chargement…" /> : null}
          <Select
            value={setId}
            onChange={(e) => {
              setSetId(e.target.value);
              setVersionId('');
            }}
          >
            <option value="">— Choisir un jeu —</option>
            {(sets.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setShowSetForm((v) => !v)}
          >
            Nouveau jeu
          </Button>
          {showSetForm ? (
            <CodeNameForm
              idPrefix="coeff-set-create"
              pending={createSet.isPending}
              onCancel={() => setShowSetForm(false)}
              onSubmit={(input) =>
                createSet.mutate(input, { onSuccess: () => setShowSetForm(false) })
              }
            />
          ) : null}
          {createSet.isError ? (
            <Alert variant="error">{toDisplayError(createSet.error).message}</Alert>
          ) : null}

          {setId ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <div className="flex items-center gap-2">
                <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                  <option value="">— Version —</option>
                  {(versions.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber ?? '?'} · {CATALOG_VERSION_STATUS_LABELS[v.status]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label htmlFor="cv-from">Valide du</Label>
                  <Input
                    id="cv-from"
                    type="date"
                    value={valFrom}
                    onChange={(e) => setValFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cv-to">au (optionnel)</Label>
                  <Input
                    id="cv-to"
                    type="date"
                    value={valTo}
                    onChange={(e) => setValTo(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  disabled={valFrom === '' || createVersion.isPending}
                  onClick={() =>
                    createVersion.mutate(
                      { validFrom: valFrom, validTo: valTo ? valTo : undefined },
                      { onSuccess: (v) => setVersionId(v.id) },
                    )
                  }
                >
                  Nouvelle version
                </Button>
              </div>
              {createVersion.isError ? (
                <Alert variant="error">{toDisplayError(createVersion.error).message}</Alert>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>
            {version ? (
              <span className="flex items-center gap-2">
                Version v{version.versionNumber ?? '?'}
                <Badge tone={version.status === 'ACTIF' ? 'success' : 'neutral'}>
                  {CATALOG_VERSION_STATUS_LABELS[version.status]}
                </Badge>
              </span>
            ) : (
              'Valeurs'
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!version ? (
            <p className="text-sm text-slate-500">
              Sélectionnez une version pour saisir les coefficients.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {(types.data ?? []).map((t) => {
                  const current = version.values?.find((v) => v.coefficientTypeId === t.id);
                  return (
                    <div key={t.id} className="flex items-center gap-2">
                      <span className="w-40 text-sm">{t.name}</span>
                      <Input
                        inputMode="decimal"
                        className="w-32"
                        defaultValue={current?.value ?? ''}
                        disabled={!isDraft}
                        onChange={(e) => setValueDraft((d) => ({ ...d, [t.id]: e.target.value }))}
                      />
                      {isDraft ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={setValue.isPending || !(valueDraft[t.id] ?? current?.value)}
                          onClick={() =>
                            setValue.mutate(
                              {
                                coefficientTypeId: t.id,
                                value: valueDraft[t.id] ?? current?.value ?? '',
                              },
                              { onSuccess: () => versionDetail.refetch() },
                            )
                          }
                        >
                          Enregistrer
                        </Button>
                      ) : (
                        <span className="text-sm text-slate-500">{current?.value ?? '—'}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {setValue.isError ? (
                <Alert variant="error">{toDisplayError(setValue.error).message}</Alert>
              ) : null}
              <div className="border-t border-slate-100 pt-3">
                <LifecycleButtons
                  entityLabel={`la version v${version.versionNumber ?? ''}`}
                  pending={transition.isPending}
                  error={transition.error}
                  onAction={onLifecycle}
                />
              </div>
            </>
          )}

          <div className="space-y-2 rounded-md bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-400">Testeur de résolution</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="rc-code">Code du jeu</Label>
                <Input
                  id="rc-code"
                  value={rSetCode}
                  onChange={(e) => setRSetCode(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="rc-date">Date</Label>
                <Input
                  id="rc-date"
                  type="date"
                  value={rDate}
                  onChange={(e) => setRDate(e.target.value)}
                />
              </div>
              <Button
                type="button"
                size="sm"
                disabled={rSetCode === '' || rDate === '' || resolve.isPending}
                onClick={() =>
                  resolve.mutate(
                    { setCode: rSetCode, valuationDate: rDate },
                    { onSuccess: (d) => setResolved(d) },
                  )
                }
              >
                Résoudre
              </Button>
            </div>
            {resolve.isError ? (
              <Alert variant="error">{toDisplayError(resolve.error).message}</Alert>
            ) : null}
            {resolved ? (
              <ul className="text-sm">
                {resolved.coefficients.map((c) => (
                  <li key={c.code}>
                    {c.code} : <strong>{c.value}</strong>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
